import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = 'https://learntok-backend-2026-24c204fe508e.herokuapp.com/dt';
const SITE_URL = 'https://refloq.com';

/* ---------- helpers ---------- */
async function api(path, { method = 'GET', body, token } = {}) {
  const r = await fetch(API + path, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Something went wrong');
  return d;
}
function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(',')[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
const hue = (s) => { let h = 0; for (const c of (s || '?')) h = (h * 31 + c.charCodeAt(0)) % 360; return h; };
const avatarStyle = (s) => ({ background: `linear-gradient(135deg, hsl(${hue(s)} 75% 60%), hsl(${(hue(s) + 45) % 360} 70% 48%))` });
const rankIcon = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`);
const isAdminPhone = (p) => (p || '').replace(/\D/g, '').endsWith('8062248515');
const fmt = (n) => (n || 0).toLocaleString();
function resizeImage(file, size = 256, quality = 0.82) {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(file); const img = new Image();
    img.onload = () => {
      const sd = Math.min(img.width, img.height);
      const c = document.createElement('canvas'); c.width = size; c.height = size;
      c.getContext('2d').drawImage(img, (img.width - sd) / 2, (img.height - sd) / 2, sd, sd, 0, 0, size, size);
      URL.revokeObjectURL(url); res(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = rej; img.src = url;
  });
}
function Avatar({ name, src, size = 42, radius = 14, style }) {
  const base = { width: size, height: size, borderRadius: radius, ...(style || {}) };
  if (src) return <div className="avatar" style={{ ...base, padding: 0, overflow: 'hidden', background: '#eee' }}><img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>;
  return <div className="avatar" style={{ ...avatarStyle(name), ...base }}>{(name || '?')[0].toUpperCase()}</div>;
}
const FAKE_NAMES = ['Alex', 'Sam', 'Jordan', 'Taylor', 'Casey', 'Riley', 'Jamie', 'Morgan', 'Chris', 'Dana', 'Lee', 'Robin', 'Drew', 'Quinn', 'Avery', 'Reese', 'Cameron', 'Devon', 'Skyler', 'Harper'];
const fakeRow = (i) => { const name = FAKE_NAMES[i % FAKE_NAMES.length] + ' ' + String.fromCharCode(65 + (i * 7) % 26) + '.'; const total = 3 + (i * 3) % 5; const done = (i * 5) % (total + 1); const streak = (i * 13) % 40; return { name, total, done, streak }; };

/* ---------- progress ring ---------- */
function Ring({ done, total, size = 72, stroke = 7, label }) {
  const pct = total ? done / total : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#e8e9f1" strokeWidth={stroke} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke="url(#rg)" strokeWidth={stroke} fill="none"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset .7s cubic-bezier(.4,0,.2,1)' }} />
      <defs><linearGradient id="rg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#17b3c4" /><stop offset="1" stopColor="#4ade80" /></linearGradient></defs>
      <text x="50%" y="46%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.26} fontWeight="700" fill="#181b2c">{done}</text>
      <text x="50%" y="68%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.15} fill="#6b7180">{label || `of ${total}`}</text>
    </svg>
  );
}

const css = `
:root{
  --bg:#f6f6fb; --text:#181b2c; --muted:#6b7180; --card:#ffffff;
  --violet:#0e7c8a; --violet2:#17b3c4; --mint:#16a34a; --amber:#c2680a; --gold:#b8860b; --danger:#e23b3b;
  --border:rgba(20,22,45,.09); --radius:22px;
}
*{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
html,body{margin:0}
body{
  background:var(--bg);color:var(--text);
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Inter,sans-serif;-webkit-font-smoothing:antialiased;
  background-image:
    radial-gradient(820px 560px at 86% -8%, rgba(14,124,138,.13), transparent 60%),
    radial-gradient(700px 540px at -12% 2%, rgba(22,179,100,.11), transparent 55%),
    radial-gradient(640px 480px at 50% 114%, rgba(255,150,80,.12), transparent 60%);
  background-attachment:fixed;
}
.dt{max-width:480px;margin:0 auto;padding:0 18px calc(56px + env(safe-area-inset-bottom));min-height:100vh;position:relative}
.dt h1{font-size:clamp(25px,7.4vw,31px);font-weight:800;letter-spacing:-.025em;margin:0;color:var(--text)}
.dt h2{font-size:15px;font-weight:700;margin:26px 0 12px;color:#3a3f55;letter-spacing:.01em}
.muted{color:var(--muted);font-size:14px;line-height:1.5}
.grad{background:linear-gradient(120deg,#0e7c8a,#0e7c8a 55%,#16a34a);-webkit-background-clip:text;background-clip:text;color:transparent}

.card{background:var(--card);border:1px solid var(--border);border-radius:var(--radius);
  padding:18px;margin:14px 0;box-shadow:0 6px 22px rgba(20,22,45,.06);animation:up .45s cubic-bezier(.2,.7,.2,1) both}
.card.tap{cursor:pointer;transition:transform .15s,box-shadow .2s,border-color .2s}
.card.tap:active{transform:scale(.985)}
.card.tap:hover{box-shadow:0 12px 32px rgba(14,124,138,.16);border-color:rgba(14,124,138,.35)}
.hero{background:linear-gradient(140deg,#efeaff,#e7f8ef);border-color:rgba(14,124,138,.18)}
.gold{background:linear-gradient(140deg,#fff6df,#fffdf6);border-color:rgba(184,134,11,.28)}

.row{display:flex;align-items:center;gap:12px}
.between{justify-content:space-between}

.btn{border:none;border-radius:15px;padding:14px 18px;font-size:15px;font-weight:700;width:100%;cursor:pointer;
  color:#fff;background:linear-gradient(135deg,#17b3c4,#0e7c8a);
  box-shadow:0 8px 22px rgba(14,124,138,.32);transition:transform .12s,box-shadow .2s,opacity .2s}
.btn:active{transform:translateY(1px) scale(.99)}
.btn:disabled{opacity:.4;box-shadow:none}
.btn.ghost{background:#f1f2f8;color:var(--violet);box-shadow:none;border:1px solid var(--border)}
.btn.sm{width:auto;padding:11px 16px;font-size:14px;border-radius:13px}

.inp{width:100%;background:#f1f2f8;border:1px solid var(--border);border-radius:15px;
  padding:14px 15px;color:var(--text);font-size:16px;outline:none;margin:7px 0;transition:.2s}
.inp::placeholder{color:#9aa0b2}
.inp:focus{border-color:var(--violet);background:#fff;box-shadow:0 0 0 3px rgba(14,124,138,.14)}
label.cap{font-size:12px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.04em}

.seg{display:flex;background:#f1f2f8;border:1px solid var(--border);border-radius:14px;padding:4px;margin:8px 0}
.seg button{flex:1;border:none;background:none;color:var(--muted);font-weight:700;font-size:14px;padding:10px;border-radius:10px;cursor:pointer;transition:.2s}
.seg button.on{background:linear-gradient(135deg,#17b3c4,#0e7c8a);color:#fff;box-shadow:0 4px 12px rgba(14,124,138,.3)}
.seg button:disabled{opacity:.4;cursor:not-allowed}
.chips{display:flex;gap:8px;overflow-x:auto;padding:8px 0 2px;-webkit-overflow-scrolling:touch}
.chips::-webkit-scrollbar{display:none}
.chip{flex:none;border:1px solid var(--border);background:#fff;color:#3a3f55;border-radius:30px;padding:10px 16px;font-weight:700;font-size:14px;cursor:pointer;white-space:nowrap}
.chip.on{background:linear-gradient(135deg,#17b3c4,#0e7c8a);color:#fff;border-color:transparent;box-shadow:0 4px 12px rgba(14,124,138,.3)}
.chip.add{padding:10px 15px}

.pill{border-radius:30px;padding:6px 13px;font-size:12px;font-weight:700;display:inline-flex;align-items:center;gap:5px}
.pill.fire{background:#fff1dc;color:var(--amber);border:1px solid #ffe2bc}
.tag{font-size:12px;color:var(--mint)}
.crewtag{font-size:11px;color:var(--violet);background:#efeaff;padding:2px 8px;border-radius:20px}
.badge{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#0f9d58;background:#e3f8ec;padding:3px 8px;border-radius:7px}

.avatar{width:42px;height:42px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;color:#fff;flex:none;box-shadow:0 3px 10px rgba(20,22,45,.18)}
.dotstack{display:flex}
.dotstack .dot{width:24px;height:24px;border-radius:50%;margin-left:-7px;border:2px solid #fff;box-shadow:0 1px 3px rgba(20,22,45,.15)}
.dotstack .dot:first-child{margin-left:0}

.todo{display:flex;align-items:center;gap:13px;padding:13px 0;border-bottom:1px solid rgba(20,22,45,.07)}
.todo:last-child{border-bottom:none}
.box{width:26px;height:26px;border:2px solid #cdd1de;border-radius:9px;flex:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:15px;color:transparent;transition:.2s}
.box.on{background:linear-gradient(135deg,#34d27a,#16a34a);border-color:transparent;color:#fff;animation:pop .3s}
.txt{flex:1;font-size:15px;line-height:1.35}
.txt.done{text-decoration:line-through;color:#a3a8ba}
.blur{filter:blur(7px);user-select:none;pointer-events:none;opacity:.7}
.icbtn{display:flex;align-items:center;justify-content:center;min-width:34px;height:34px;border-radius:10px;background:#f1f2f8;border:1px solid var(--border);cursor:pointer;font-size:15px;color:#3a3f55}
.x{color:#b3b8c6;cursor:pointer;font-size:20px;padding:0 4px;line-height:1}

.bar{height:7px;background:#ecedf4;border-radius:7px;overflow:hidden;margin-top:9px}
.bar>i{display:block;height:100%;border-radius:7px;background:linear-gradient(90deg,#0e7c8a,#16a34a);transition:width .6s ease}

.winners{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;text-align:center}
/* prominent crew champions */
.champs{margin:14px 0;padding:18px 14px;border-radius:22px;background:linear-gradient(135deg,#17b3c4,#0e7c8a);color:#fff;box-shadow:0 14px 34px rgba(14,124,138,.38)}
.champs-h{font-weight:800;font-size:18px;text-align:center;margin-bottom:14px;letter-spacing:-.01em}
.champs-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:9px;text-align:center}
.champ{background:rgba(255,255,255,.15);border-radius:16px;padding:13px 6px}
.champ-l{font-size:10.5px;opacity:.85;margin-bottom:9px;font-weight:700;text-transform:uppercase;letter-spacing:.04em}
.champ-av{width:48px;height:48px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:19px;margin:0 auto;border:2px solid rgba(255,255,255,.7);color:#fff}
.champ-n{font-weight:700;font-size:13px;margin-top:7px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.champ-c{font-size:11px;opacity:.92;margin-top:2px;font-weight:600}
.champ-empty{font-size:22px;opacity:.55;padding-top:16px}

.wcol{background:#fbfaff;border:1px solid var(--border);border-radius:16px;padding:13px 6px}
.wcol .av{width:38px;height:38px;border-radius:12px;margin:0 auto;display:flex;align-items:center;justify-content:center;font-weight:800;color:#fff}

.share{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:8px;margin-top:12px}
.share a,.share button{display:flex;flex-direction:column;align-items:center;gap:6px;padding:13px 4px;border-radius:15px;background:#f1f2f8;border:1px solid var(--border);color:#3a3f55;font-size:11px;font-weight:600;cursor:pointer;text-decoration:none}
.share .ic{font-size:21px;line-height:1}
.linkbox{display:flex;align-items:center;gap:8px;background:#f7f8fc;border:1px dashed #c9cddd;border-radius:13px;padding:11px 13px;font-size:13px;color:#4a5060;word-break:break-all;cursor:pointer}

.err{color:var(--danger);font-size:13px;margin:8px 0}
.link{color:var(--violet);cursor:pointer;font-size:14px;font-weight:600}
.center{text-align:center}
.toast{position:fixed;left:50%;bottom:calc(90px + env(safe-area-inset-bottom));transform:translateX(-50%);background:#181b2c;color:#fff;border:1px solid rgba(255,255,255,.1);padding:13px 20px;border-radius:30px;font-size:14px;font-weight:600;z-index:60;max-width:90%;box-shadow:0 12px 30px rgba(20,22,45,.35);animation:up .3s}
.topbar{position:sticky;top:0;z-index:20;display:flex;align-items:center;justify-content:space-between;padding:16px 0 12px;background:linear-gradient(var(--bg),rgba(246,246,251,.7) 70%,transparent);backdrop-filter:blur(6px)}
.brand{font-weight:800;font-size:18px;letter-spacing:-.01em;color:var(--text)}
.empty{text-align:center;color:var(--muted);padding:18px 8px}

.nav{position:fixed;left:0;right:0;bottom:0;z-index:40;display:flex;justify-content:center;padding:8px 0 calc(8px + env(safe-area-inset-bottom));background:rgba(255,255,255,.92);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border-top:1px solid var(--border)}
.nav-inner{display:flex;gap:4px;max-width:480px;width:100%;padding:0 18px}
.tabbtn{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;padding:7px;border-radius:14px;cursor:pointer;color:#9aa0b2;font-size:11px;font-weight:700;background:none;border:none;transition:color .2s}
.tabbtn.on{color:var(--violet)}
.tabbtn .ic{font-size:21px;line-height:1}
.has-nav{padding-bottom:calc(92px + env(safe-area-inset-bottom))}
/* StepUp-style header + period tabs */
.appbar{position:sticky;top:0;z-index:30;margin:0 -18px 6px;padding:14px 18px 0;background:var(--violet);color:#fff;box-shadow:0 6px 18px rgba(14,124,138,.25)}
.appbar .barrow{display:flex;align-items:center;justify-content:space-between;gap:10px}
.appbar .title{font-weight:800;font-size:19px;letter-spacing:-.01em;flex:1;text-align:center}
.appbar .ic{width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:19px;cursor:pointer}
.ptabs{display:flex;gap:5px;margin-top:13px;padding-bottom:13px}
.ptab{flex:1;text-align:center;padding:9px 4px;border-radius:12px;font-weight:700;font-size:13.5px;color:rgba(255,255,255,.82);cursor:pointer;transition:all .15s}
.ptab.on{background:#fff;color:var(--violet);box-shadow:0 3px 10px rgba(0,0,0,.12)}
/* hero */
.hero2{text-align:center;padding:16px 0 4px}
.hero2 .lbl{color:var(--muted);font-size:15px;font-weight:500}
.hero2 .big{font-size:clamp(50px,17vw,74px);font-weight:800;letter-spacing:-.03em;line-height:1.04;display:flex;align-items:center;justify-content:center;gap:12px}
.hero2 .ck{color:#16a34a;font-size:.5em;font-weight:900}
.hero2 .stats{color:var(--muted);font-weight:600;margin-top:6px;font-size:15px}
/* clean leaderboard */
.lb{background:#fff;border:1px solid var(--border);border-radius:var(--radius);box-shadow:0 6px 22px rgba(20,22,45,.06);overflow:hidden;margin-top:10px}
.lbrow{display:flex;align-items:center;gap:14px;padding:14px 16px;border-bottom:1px solid rgba(20,22,45,.06)}
.lbrow:last-child{border-bottom:none}
.lbrow .rank{min-width:24px;text-align:center;font-weight:800;color:#9aa0b2}
.lbrow .nm{flex:1;font-weight:700;font-size:17px;letter-spacing:-.01em}
.lbrow .nm small{font-weight:600;color:var(--muted)}
.lbrow .val{text-align:right;line-height:1.15}
.lbrow .val b{font-size:19px;letter-spacing:-.01em}
.lbrow .val span{display:block;font-size:11px;color:var(--muted);margin-top:1px}
.lbtasks{padding:2px 16px 12px 58px;background:#fafbfc;border-bottom:1px solid rgba(20,22,45,.06)}
.lbtasks .todo{padding:7px 0}
.avatar.lg{width:52px;height:52px;border-radius:50%;font-size:19px}


.sp{display:flex;gap:10px;margin-top:18px}
.spcard{flex:1;text-align:center;background:var(--card);border:1px solid var(--border);border-radius:18px;padding:14px 6px;box-shadow:0 5px 18px rgba(20,22,45,.05)}
.spcard b{display:block;font-size:clamp(18px,5.5vw,24px);font-weight:800;color:var(--violet)}
.spcard span{font-size:11px;color:var(--muted)}
.steps .step{display:flex;gap:14px;align-items:flex-start;padding:12px 0;border-bottom:1px solid rgba(20,22,45,.07)}
.steps .step:last-child{border-bottom:none}
.stepn{width:32px;height:32px;border-radius:11px;flex:none;display:flex;align-items:center;justify-content:center;font-weight:800;background:linear-gradient(135deg,#17b3c4,#0e7c8a);color:#fff;font-size:14px}
.lockwrap{position:relative;overflow:hidden}
.lockwrap .locked{filter:blur(7px);pointer-events:none;user-select:none}
.lockover{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;text-align:center;font-weight:800;color:var(--text)}
.lockover .lk{font-size:30px}

.statgrid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.stat{background:var(--card);border:1px solid var(--border);border-radius:18px;padding:16px;box-shadow:0 5px 18px rgba(20,22,45,.05)}
.stat b{font-size:26px;font-weight:800;display:block}

@keyframes up{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.switch{position:relative;width:46px;height:28px;border-radius:30px;background:#d7dae6;transition:.2s;cursor:pointer;flex:none}
.switch.on{background:linear-gradient(135deg,#17b3c4,#0e7c8a)}
.switch i{position:absolute;top:3px;left:3px;width:22px;height:22px;border-radius:50%;background:#fff;transition:.2s;box-shadow:0 1px 3px rgba(0,0,0,.25)}
.switch.on i{left:21px}
.rowset{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 0;border-bottom:1px solid rgba(20,22,45,.07)}
.rowset:last-child{border-bottom:none}
select.inp{appearance:none;-webkit-appearance:none;-moz-appearance:none}
.inp.cc{width:120px;flex:none;padding-left:12px;padding-right:8px;font-size:15px}
.switch.sm{width:38px;height:22px}
.switch.sm i{width:16px;height:16px;top:3px;left:3px}
.switch.sm.on i{left:19px}
@keyframes pop{0%{transform:scale(.5)}60%{transform:scale(1.15)}100%{transform:scale(1)}}
`;

const COUNTRIES = [
  ['+1', '🇺🇸'], ['+1', '🇨🇦'], ['+44', '🇬🇧'], ['+91', '🇮🇳'], ['+61', '🇦🇺'], ['+971', '🇦🇪'],
  ['+49', '🇩🇪'], ['+33', '🇫🇷'], ['+34', '🇪🇸'], ['+39', '🇮🇹'], ['+31', '🇳🇱'], ['+353', '🇮🇪'],
  ['+55', '🇧🇷'], ['+52', '🇲🇽'], ['+54', '🇦🇷'], ['+65', '🇸🇬'], ['+81', '🇯🇵'], ['+82', '🇰🇷'],
  ['+86', '🇨🇳'], ['+852', '🇭🇰'], ['+92', '🇵🇰'], ['+880', '🇧🇩'], ['+62', '🇮🇩'], ['+63', '🇵🇭'],
  ['+60', '🇲🇾'], ['+66', '🇹🇭'], ['+84', '🇻🇳'], ['+27', '🇿🇦'], ['+234', '🇳🇬'], ['+254', '🇰🇪'],
  ['+966', '🇸🇦'], ['+972', '🇮🇱'], ['+90', '🇹🇷'], ['+7', '🇷🇺'], ['+64', '🇳🇿'], ['+46', '🇸🇪'],
  ['+47', '🇳🇴'], ['+45', '🇩🇰'], ['+41', '🇨🇭'], ['+351', '🇵🇹'], ['+48', '🇵🇱'], ['+20', '🇪🇬'],
];

/* ---------- reusable auth form ---------- */
function AuthForm({ onLogin, cta = 'Send code →' }) {
  const [mode, setMode] = useState('signup');   // 'signup' | 'login'
  const [step, setStep] = useState('phone');
  const [cc, setCc] = useState('+1');
  const [phone, setPhone] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [avatar, setAvatar] = useState('');
  const onPickAvatar = async (e) => { const f = e.target.files && e.target.files[0]; if (!f) return; try { setAvatar(await resizeImage(f)); } catch (err) { } };
  const swap = (m) => { setMode(m); setStep('phone'); setErr(''); setOtp(''); };
  const send = async () => {
    setErr(''); setBusy(true);
    const full = cc + phone.replace(/\D/g, '');
    try { await api('/send-otp', { method: 'POST', body: { phone: full, mode } }); setSentTo(full); setStep('otp'); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const verify = async () => {
    setErr(''); setBusy(true);
    try {
      const body = { phone: sentTo, otp };
      if (mode === 'signup') { body.name = name; if (avatar) body.avatar = avatar; }
      const d = await api('/verify-otp', { method: 'POST', body });
      onLogin(d.token, d.user);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const sendLabel = mode === 'login' ? 'Log in →' : cta;
  return (
    <div className="card hero">
      <div className="seg" style={{ marginTop: 0 }}>
        <button className={mode === 'signup' ? 'on' : ''} onClick={() => swap('signup')}>Sign up</button>
        <button className={mode === 'login' ? 'on' : ''} onClick={() => swap('login')}>Log in</button>
      </div>
      {step === 'phone' ? (
        <>
          <label className="cap">Phone number</label>
          <div className="row" style={{ gap: 8 }}>
            <select className="inp cc" value={cc} onChange={e => setCc(e.target.value)}>
              {COUNTRIES.map((c, i) => <option key={i} value={c[0]}>{c[1]} {c[0]}</option>)}
            </select>
            <input className="inp" style={{ margin: 0, flex: 1 }} type="tel" placeholder="555 000 0000" value={phone} onChange={e => setPhone(e.target.value)} />
          </div>
          {err && <div className="err">{err}</div>}
          <button className="btn" disabled={busy || !phone} onClick={send}>{busy ? 'Sending…' : sendLabel}</button>
        </>
      ) : (
        <>
          <label className="cap">Code sent to {sentTo}</label>
          <input className="inp" inputMode="numeric" placeholder="6-digit code" value={otp} onChange={e => setOtp(e.target.value)} />
          {mode === 'signup' && (
            <div className="row" style={{ gap: 12, margin: '7px 0' }}>
              <Avatar name={name} src={avatar} size={52} radius={16} />
              <label className="btn ghost sm" style={{ width: 'auto' }}>{avatar ? 'Change photo' : 'Add photo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickAvatar} />
              </label>
            </div>
          )}
          {mode === 'signup' && <input className="inp" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />}
          {err && <div className="err">{err}</div>}
          <button className="btn" disabled={busy || !otp || (mode === 'signup' && !name.trim())} onClick={verify}>{busy ? 'Verifying…' : (mode === 'login' ? 'Log in' : 'Create account')}</button>
          <p className="link center" onClick={() => setStep('phone')} style={{ marginTop: 14 }}>← Change number</p>
        </>
      )}
    </div>
  );
}

/* ---------- root ---------- */
export default function App() {
  const [token, setToken] = useState(localStorage.getItem('dt_token') || '');
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('dt_user') || 'null'));
  const [view, setView] = useState('loading');
  const [activeGroup, setActiveGroup] = useState(null);
  const [checkinToken, setCheckinToken] = useState('');
  const [pendingJoin, setPendingJoin] = useState('');
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2200); };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('admin') === 'true' || p.get('admin') === '1') { setView('admin'); return; }
    const ci = p.get('checkin');
    if (ci) { setCheckinToken(ci); setView('checkin'); return; }
    const g = p.get('group');
    if (g) setPendingJoin(g.toUpperCase());
    setView(token ? 'app' : 'landing');
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!token || !pendingJoin) return;
    (async () => {
      try {
        const d = await api('/join-group', { method: 'POST', token, body: { code: pendingJoin } });
        flash('Joined ' + d.group.name); setActiveGroup(d.group); setView('group');
      } catch (e) { flash(e.message); setView('app'); }
      finally { setPendingJoin(''); window.history.replaceState({}, '', window.location.pathname); }
    })();
  }, [token, pendingJoin]); // eslint-disable-line

  const login = (tok, usr) => {
    localStorage.setItem('dt_token', tok); localStorage.setItem('dt_user', JSON.stringify(usr));
    setToken(tok); setUser(usr);
    if (!pendingJoin) { setView('app'); }
  };
  const updateUser = (u) => { setUser(u); localStorage.setItem('dt_user', JSON.stringify(u)); };
  const logout = () => { localStorage.clear(); setToken(''); setUser(null); setView('landing'); };

  return (
    <div className="dt">
      <style>{css}</style>
      {view === 'loading' && <p className="muted" style={{ marginTop: 40 }}>Loading…</p>}
      {view === 'landing' && <Landing onLogin={login} inviteCode={pendingJoin} flash={flash} onPickGroup={(code) => setPendingJoin(code)} />}
      {view === 'app' && <Home token={token} user={user} openGroup={(g) => { setActiveGroup(g); setView('group'); }} onSettings={() => setView('settings')} flash={flash} />}
      {view === 'group' && <Group token={token} group={activeGroup} back={() => setView('app')} flash={flash} />}
      {view === 'settings' && <Settings token={token} user={user} flash={flash} onUpdateUser={updateUser} logout={logout} back={() => setView('app')} />}
      {view === 'checkin' && <Checkin token={checkinToken} flash={flash} />}
      {view === 'admin' && <Admin />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

/* ---------- landing ---------- */
function Landing({ onLogin, inviteCode, flash, onPickGroup }) {
  const [preview, setPreview] = useState(null);
  const [pub, setPub] = useState([]);
  const [shown, setShown] = useState(14);
  const sentinel = useRef(null);
  useEffect(() => { if (inviteCode) api('/public-preview/' + inviteCode).then(setPreview).catch(() => { }); }, [inviteCode]);
  useEffect(() => { api('/discover').then(d => setPub(d.groups)).catch(() => { }); }, []);
  useEffect(() => {
    if (!inviteCode || !preview || !preview.isPublic || !sentinel.current) return;
    const io = new IntersectionObserver(es => { if (es[0].isIntersecting) setShown(n => Math.min(n + 10, preview.totalMembers || n)); });
    io.observe(sentinel.current); return () => io.disconnect();
  }, [inviteCode, preview]);
  const sharePub = async (code) => { try { await navigator.clipboard.writeText(`${SITE_URL}/?group=${code}`); flash && flash('Link copied'); } catch { flash && flash('Copy failed'); } };

  if (inviteCode) {
    const real = (preview && preview.members) || [];
    const total = (preview && preview.totalMembers) || 0;
    const isPub = preview && preview.isPublic;
    return (
      <div style={{ paddingTop: 30 }}>
        <div className="center">
          <div style={{ fontSize: 48, filter: 'drop-shadow(0 6px 20px rgba(14,124,138,.5))' }}>🌙</div>
          <h1 style={{ marginTop: 10 }}>{preview ? preview.name : 'You’re invited'}</h1>
          <p className="muted" style={{ marginTop: 6 }}>{total ? <>🔥 {fmt(total)} members crushing their goals</> : 'Join the crew on Refloq'}</p>
        </div>
        <p className="muted center" style={{ marginTop: 8, marginBottom: 0 }}>Register to claim your spot & start your list.</p>
        {isPub && <Champions winners={preview && preview.winners} />}
        <AuthForm onLogin={onLogin} cta="Register & join →" />
        {isPub ? (
          <>
            <h2>Who’s in this crew</h2>
            <PreviewList members={real} total={total} shown={shown} sentinelRef={sentinel} />
          </>
        ) : (
          <div className="card lockwrap">
            <div className="locked">{[1, 2, 3].map(i => (
              <div className="row between" key={i} style={{ padding: '10px 0' }}>
                <div className="row"><div className="avatar" style={avatarStyle('crew' + i)}>A</div><div><b>Member {i}</b><div className="muted">4/5 done</div></div></div>
                <span className="pill fire">🔥 9</span></div>))}</div>
            <div className="lockover"><div className="lk">🔒</div><span>Register to unlock<br />tonight’s leaderboard</span></div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ paddingTop: 34 }}>
      <div className="center">
        <div style={{ fontSize: 58, filter: 'drop-shadow(0 6px 22px rgba(14,124,138,.55))' }}>🌙</div>
        <h1 style={{ marginTop: 12 }}><span className="grad">Refloq</span></h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 16 }}>Get your to-dos done with a crew that keeps you honest. Private lists, public progress.</p>
      </div>
      <div className="sp">
        <div className="spcard"><b>12.4k</b><span>tasks crushed</span></div>
        <div className="spcard"><b>3,200</b><span>streaks alive</span></div>
        <div className="spcard"><b>890</b><span>active crews</span></div>
      </div>
      <AuthForm onLogin={onLogin} cta="Get started →" />
      {pub.length > 0 && (
        <>
          <h2>Join a public crew</h2>
          {pub.map(g => (
            <div key={g._id} className="card row between">
              <div><div className="row" style={{ gap: 8 }}><b>{g.name}</b><span className="badge">Public</span></div>
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>🔥 {fmt(g.memberCount)} members</div></div>
              <div className="row" style={{ gap: 6 }}>
                <button className="icbtn" onClick={() => sharePub(g.code)}>🔗</button>
                <button className="btn sm" onClick={() => onPickGroup(g.code)}>Join</button>
              </div>
            </div>
          ))}
        </>
      )}
      <h2>How it works</h2>
      <div className="card steps">
        <div className="step"><div className="stepn">1</div><div><b>Make a crew</b><div className="muted">Create one and share a link with friends.</div></div></div>
        <div className="step"><div className="stepn">2</div><div><b>Add your list</b><div className="muted">Your daily to-dos — private to you.</div></div></div>
        <div className="step"><div className="stepn">3</div><div><b>Nightly check-in</b><div className="muted">Get a text, tap off what you finished.</div></div></div>
        <div className="step"><div className="stepn">4</div><div><b>Prove it</b><div className="muted">AI verifies with a photo. Streaks & ranks keep you going.</div></div></div>
      </div>
      <div className="card hero center">
        <div style={{ fontSize: 26 }}>🔒</div><b>Your list stays yours.</b>
        <p className="muted" style={{ margin: '6px 0 0' }}>Crewmates see your progress (4/5 done) — never the actual tasks.</p>
      </div>
    </div>
  );
}

/* ---------- HOME (StepUp-style: period tabs + hero + crews + leaderboard) ---------- */
function Home({ token, user, openGroup, onSettings, flash }) {
  const [groups, setGroups] = useState([]);
  const [pub, setPub] = useState([]);
  const [sel, setSel] = useState(null);
  const [board, setBoard] = useState(null);
  const [period, setPeriod] = useState('today');
  const [text, setText] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const admin = isAdminPhone(user && user.phone);

  const loadGroups = useCallback(async () => {
    try { const d = await api('/my-groups', { token }); setGroups(d.groups); } catch (e) { }
    try { const p = await api('/public-groups', { token }); setPub(p.groups); } catch (e) { }
  }, [token]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  useEffect(() => {
    if (groups.length === 0) { setSel(null); return; }
    if (!sel || !groups.find(g => g._id === sel)) setSel(groups[0]._id);
  }, [groups]); // eslint-disable-line

  const loadBoard = useCallback(async () => {
    if (!sel) { setBoard(null); return; }
    try { const d = await api('/group/' + sel, { token }); setBoard(d); } catch (e) { flash(e.message); }
  }, [sel, token]); // eslint-disable-line
  useEffect(() => { loadBoard(); }, [loadBoard]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') loadBoard(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadBoard]);

  const me = board && board.members ? board.members.find(m => m.isMe) : null;
  const selCrew = groups.find(g => g._id === sel) || (board && board.group);
  const cnt = (m) => (m && m.counts ? (m.counts[period] || 0) : (m ? (m.done || 0) : 0));
  const myCount = me ? cnt(me) : 0;
  const sorted = (board && board.members ? board.members.slice() : []).sort((a, b) => cnt(b) - cnt(a) || (a.name || '').localeCompare(b.name || ''));
  const periodLabel = { today: 'today', yesterday: 'yesterday', week: 'this week', month: 'this month' }[period];

  const add = async () => { if (!text.trim() || !sel) return; try { await api('/add-todo', { method: 'POST', token, body: { groupId: sel, text } }); setText(''); loadBoard(); } catch (e) { flash(e.message); } };
  const toggle = async (t) => { try { await api('/toggle-todo', { method: 'POST', token, body: { todoId: t.id, done: !t.done } }); loadBoard(); } catch (e) { flash(e.message); } };
  const del = async (t) => { try { await api('/delete-todo', { method: 'POST', token, body: { todoId: t.id } }); loadBoard(); } catch (e) { flash(e.message); } };
  const toggleBlur = async (t) => { try { await api('/toggle-blur', { method: 'POST', token, body: { todoId: t.id, blurred: !t.blurred } }); loadBoard(); } catch (e) { flash(e.message); } };
  const verify = async (t, file) => { flash('Checking photo'); try { const imageBase64 = await fileToB64(file); const d = await api('/verify-photo', { method: 'POST', token, body: { todoId: t.id, imageBase64, mediaType: file.type } }); flash(d.verified ? 'Verified!' : (d.reason || 'Not verified')); loadBoard(); } catch (e) { flash(e.message); } };
  const create = async () => { try { await api('/create-group', { method: 'POST', token, body: { name, isPublic } }); setName(''); setIsPublic(false); setShowAdd(false); loadGroups(); } catch (e) { flash(e.message); } };
  const join = async (c) => { try { const d = await api('/join-group', { method: 'POST', token, body: { code: c } }); setCode(''); setShowAdd(false); flash('Joined ' + d.group.name); loadGroups(); } catch (e) { flash(e.message); } };

  const discover = pub.filter(g => !g.joined);
  const shareUrl = selCrew ? (SITE_URL + '/?group=' + selCrew.code) : '';
  const invite = async () => { if (!selCrew) return; try { if (navigator.share) await navigator.share({ title: selCrew.name, text: 'Join my crew on Refloq', url: shareUrl }); else { await navigator.clipboard.writeText(shareUrl); flash('Invite link copied'); } } catch (e) { } };

  return (
    <>
      <div className="appbar">
        <div className="barrow">
          <span className="ic" onClick={onSettings}>&#9881;&#65039;</span>
          <span className="title">&#127769; Refloq</span>
          <span className="ic" onClick={onSettings}>{user && user.avatar ? <img src={user.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', display: 'block' }} /> : '\uD83D\uDD25'}</span>
        </div>
        <div className="ptabs">
          {['today', 'yesterday', 'week', 'month'].map(p => (
            <div key={p} className={'ptab' + (period === p ? ' on' : '')} onClick={() => setPeriod(p)}>{p[0].toUpperCase() + p.slice(1)}</div>
          ))}
        </div>
      </div>

      <div className="hero2">
        <div className="lbl">tasks done {periodLabel}</div>
        <div className="big">{myCount > 0 && <span className="ck">&#10003;</span>}{myCount}</div>
        <div className="stats">&#128293; {(user && user.streak) || 0} &middot; {groups.length} crew{groups.length !== 1 ? 's' : ''}{me ? ' \u00b7 ' + me.done + '/' + me.total + ' today' : ''}</div>
      </div>

      <div className="chips">
        {groups.map(g => (<button key={g._id} className={'chip' + (g._id === sel ? ' on' : '')} onClick={() => setSel(g._id)}>{g.name}</button>))}
        <button className="chip add" onClick={() => setShowAdd(v => !v)}>&#65291;</button>
      </div>

      {showAdd && (
        <div className="card">
          <input className="inp" placeholder="New crew name" value={name} onChange={e => setName(e.target.value)} />
          {admin ? (<div className="seg"><button className={!isPublic ? 'on' : ''} onClick={() => setIsPublic(false)}>&#128274; Private</button><button className={isPublic ? 'on' : ''} onClick={() => setIsPublic(true)}>&#127760; Public</button></div>) : null}
          <button className="btn" disabled={!name} onClick={create}>&#65291; Create crew</button>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <input className="inp" style={{ margin: 0, flex: 1 }} placeholder="Join code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} />
            <button className="btn sm ghost" disabled={!code} onClick={() => join(code)}>Join</button>
          </div>
        </div>
      )}

      {groups.length === 0 && !showAdd && (
        <div className="card center"><div style={{ fontSize: 34 }}>&#127769;</div><p className="muted" style={{ margin: '8px 0 0' }}>No crews yet. Tap &#65291; to create or join one.</p></div>
      )}

      {selCrew && <Champions winners={board && board.winners} />}

      {selCrew && period === 'today' && (
        <>
          <div className="row between" style={{ marginTop: 10 }}>
            <h2 style={{ margin: 0 }}>Your list today</h2>
            <span className="link" onClick={invite}>Invite &#65291;</span>
          </div>
          <div className="card">
            <div className="row" style={{ gap: 8 }}>
              <input className="inp" style={{ margin: 0, flex: 1 }} placeholder="Add a task" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} />
              <button className="btn sm" onClick={add}>Add</button>
            </div>
            {me && me.todos.map(t => (
              <div className="todo" key={t.id}>
                <div className={'box' + (t.done ? ' on' : '')} onClick={() => toggle(t)}>{t.done ? '\u2713' : ''}</div>
                <span className={'txt' + (t.done ? ' done' : '')}>{t.text}</span>
                {t.verified && <span className="tag">&#9989;</span>}
                {t.done && !t.verified && (<label className="icbtn">&#128247;<input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => e.target.files[0] && verify(t, e.target.files[0])} /></label>)}
                <span className="row" style={{ gap: 5, flex: 'none' }}><span style={{ fontSize: 14 }}>{t.blurred ? '\uD83D\uDE48' : '\uD83D\uDC41\uFE0F'}</span><div className={'switch sm' + (t.blurred ? ' on' : '')} onClick={() => toggleBlur(t)}><i /></div></span>
                <span className="x" onClick={() => del(t)}>&times;</span>
              </div>
            ))}
            {me && me.todos.length === 0 && <p className="empty" style={{ paddingBottom: 0 }}>Nothing yet, add your first task above.</p>}
          </div>
        </>
      )}

      {selCrew && (
        <>
          <h2>{selCrew.name} leaderboard</h2>
          <div className="lb">
            {sorted.map((m, i) => (
              <React.Fragment key={m.id}>
                <div className="lbrow">
                  <div className="rank" style={i < 3 ? { fontSize: 18 } : null}>{rankIcon(i)}</div>
                  <Avatar name={m.name} src={m.avatar} size={52} radius={26} />
                  <div className="nm">{m.name}{m.isMe ? <small> &middot; you</small> : ''}</div>
                  <div className="val">{cnt(m) > 0 ? <><b>{cnt(m)}</b><span>{periodLabel}</span></> : <><b style={{ color: '#c0c5d2' }}>&ndash;</b><span>no update yet</span></>}</div>
                </div>
                {period === 'today' && !m.isMe && m.todos && m.todos.length > 0 && (
                  <div className="lbtasks">
                    {m.todos.map(t => (
                      <div className="todo" key={t.id}>
                        <div className={'box' + (t.done ? ' on' : '')}>{t.done ? '\u2713' : ''}</div>
                        {t.text ? <span className={'txt' + (t.done ? ' done' : '')}>{t.text}</span> : <span className="txt blur">private task</span>}
                        {t.verified && <span className="tag">&#9989;</span>}
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
            {!board && <div className="lbrow"><span className="muted">Loading</span></div>}
          </div>
        </>
      )}

      {discover.length > 0 && (
        <>
          <h2>Discover public crews</h2>
          {discover.map(g => (
            <div key={g._id} className="card tap row between" onClick={() => openGroup(g)}>
              <div>
                <div className="row" style={{ gap: 8 }}><b>{g.name}</b><span className="badge">Public</span></div>
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>&#128293; {fmt(g.memberCount)} members</div>
              </div>
              <button className="btn sm" onClick={(e) => { e.stopPropagation(); join(g.code); }}>Join</button>
            </div>
          ))}
        </>
      )}
    </>
  );
}

/* ---------- shared preview list (real members + blurred filler) ---------- */
function PreviewList({ members, total, shown, sentinelRef }) {
  const real = members || [];
  const fillerEnd = Math.min(shown, total || real.length);
  return (
    <>
      {real.map((m, i) => (
        <div key={m.id} className="card">
          <div className="row between">
            <div className="row">
              <div style={{ width: 22, textAlign: 'center', fontWeight: 800, fontSize: i < 3 ? 18 : 14, color: '#9aa0b2' }}>{rankIcon(i)}</div>
              <Avatar name={m.name} src={m.avatar} />
              <div><div style={{ fontWeight: 700 }}>{m.name}</div>
                <div className="muted" style={{ fontSize: 13 }}>{m.done}/{m.total} done{m.verified ? ` · ${m.verified} ✅` : ''}</div></div>
            </div>
            <span className="pill fire">🔥 {m.streak}</span>
          </div>
          <div className="bar"><i style={{ width: (m.total ? (m.done / m.total) * 100 : 0) + '%' }} /></div>
          {m.todos && m.todos.length > 0 && (
            <div style={{ marginTop: 12 }}>{m.todos.map(t => (
              <div className="todo" key={t.id}>
                <div className={'box' + (t.done ? ' on' : '')}>{t.done ? '✓' : ''}</div>
                {t.text ? <span className={'txt' + (t.done ? ' done' : '')}>{t.text}</span> : <span className="txt blur">private task</span>}
                {t.verified && <span className="tag">✅</span>}
              </div>))}</div>
          )}
        </div>
      ))}
      {Array.from({ length: Math.max(0, fillerEnd - real.length) }).map((_, k) => {
        const i = real.length + k; const f = fakeRow(i);
        return (
          <div key={'f' + i} className="card" style={{ filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' }}>
            <div className="row between">
              <div className="row"><div className="avatar" style={avatarStyle('f' + i)}>{f.name[0]}</div>
                <div><div style={{ fontWeight: 700 }}>{f.name}</div><div className="muted" style={{ fontSize: 13 }}>{f.done}/{f.total} done</div></div></div>
              <span className="pill fire">🔥 {f.streak}</span>
            </div>
            <div className="bar"><i style={{ width: (f.total ? (f.done / f.total) * 100 : 0) + '%' }} /></div>
          </div>
        );
      })}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </>
  );
}

/* ---------- GROUP ---------- */
function Champions({ winners }) {
  if (!winners) return null;
  const cols = [['Yesterday', winners.yesterday], ['This week', winners.week], ['This month', winners.month]];
  if (!cols.some(c => c[1])) return null;
  return (
    <div className="champs">
      <div className="champs-h">&#127942; Crew Champions</div>
      <div className="champs-grid">
        {cols.map(([label, w]) => (
          <div className="champ" key={label}>
            <div className="champ-l">{label}</div>
            {w ? (
              <>
                <div className="champ-av" style={avatarStyle(w.name)}>{w.name[0].toUpperCase()}</div>
                <div className="champ-n">{w.name}</div>
                <div className="champ-c">{w.count} done</div>
              </>
            ) : <div className="champ-empty">&ndash;</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Group({ token, group, back, flash }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [shown, setShown] = useState(14);
  const sentinel = useRef(null);

  const load = useCallback(async () => {
    try { const d = await api('/group/' + group._id, { token }); setData(d); } catch (e) { flash(e.message); }
  }, [group._id, token]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);
  useEffect(() => {
    if (!data || !data.preview || !sentinel.current) return;
    const io = new IntersectionObserver(es => { if (es[0].isIntersecting) setShown(n => Math.min(n + 10, data.totalMembers || n)); });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [data]);
  const join = async () => { try { await api('/join-group', { method: 'POST', token, body: { code: group.code } }); flash('Joined ' + group.name); setShown(14); load(); } catch (e) { flash(e.message); } };

  const me = data?.members.find(m => m.isMe);
  const add = async () => { if (!text.trim()) return; try { await api('/add-todo', { method: 'POST', token, body: { groupId: group._id, text } }); setText(''); load(); } catch (e) { flash(e.message); } };
  const toggle = async (t) => { try { await api('/toggle-todo', { method: 'POST', token, body: { todoId: t.id, done: !t.done } }); load(); } catch (e) { flash(e.message); } };
  const del = async (t) => { try { await api('/delete-todo', { method: 'POST', token, body: { todoId: t.id } }); load(); } catch (e) { flash(e.message); } };
  const toggleBlur = async (t) => { try { await api('/toggle-blur', { method: 'POST', token, body: { todoId: t.id, blurred: !t.blurred } }); load(); } catch (e) { flash(e.message); } };
  const verify = async (t, file) => { flash('🔍 Checking photo…'); try { const imageBase64 = await fileToB64(file); const d = await api('/verify-photo', { method: 'POST', token, body: { todoId: t.id, imageBase64, mediaType: file.type } }); flash(d.verified ? '✅ Verified!' : '❌ ' + (d.reason || 'Not verified')); load(); } catch (e) { flash(e.message); } };

  const shareUrl = `${SITE_URL}/?group=${group.code}`;
  const shareText = `Join my crew "${group.name}" on Refloq 🌙 — let's keep each other accountable.`;
  const full = `${shareText} ${shareUrl}`;
  const copy = async () => { try { await navigator.clipboard.writeText(full); flash('Invite copied'); } catch { flash('Copy failed'); } };
  const nativeShare = async () => { try { if (navigator.share) await navigator.share({ title: group.name, text: shareText, url: shareUrl }); else copy(); } catch { /* cancelled */ } };

  const w = data?.winners;

  if (data && data.preview) {
    const real = data.members || [];
    const total = data.totalMembers || real.length;
    return (
      <div style={{ paddingBottom: 40 }}>
        <div className="topbar"><span className="link" onClick={back}>‹ Crews</span><span className="badge">Public</span></div>
        <div className="card hero">
          <h1 style={{ fontSize: 22 }}>{group.name}</h1>
          <p className="muted" style={{ margin: '6px 0 12px' }}>🔥 {fmt(total)} members crushing their goals</p>
          <button className="btn" onClick={join}>Join this crew</button>
        </div>
        <PreviewList members={real} total={total} shown={shown} sentinelRef={sentinel} />
        <button className="btn" style={{ marginTop: 8 }} onClick={join}>Join to see everyone</button>
      </div>
    );
  }
  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="topbar">
        <span className="link" onClick={back}>‹ Crews</span>
        <span className="link" onClick={() => setShowInvite(s => !s)}>Invite ＋</span>
      </div>

      <div className="card hero row between">
        <div>
          <div className="row" style={{ gap: 8 }}><h1 style={{ fontSize: 22 }}>{group.name}</h1>{group.isPublic && <span className="badge">Public</span>}</div>
          <p className="muted" style={{ margin: '6px 0 0' }}>Tonight’s leaderboard · tasks stay private</p>
          {data?.members?.length > 0 && (
            <div className="dotstack" style={{ marginTop: 10 }}>
              {data.members.slice(0, 6).map(m => (
                m.avatar
                  ? <span key={m.id} className="dot" title={m.name} style={{ overflow: 'hidden', padding: 0 }}><img src={m.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></span>
                  : <span key={m.id} className="dot" style={avatarStyle(m.name)} title={m.name} />
              ))}
            </div>
          )}
        </div>
        <Ring done={me?.done || 0} total={me?.total || 0} label="today" />
      </div>

      {showInvite && (
        <div className="card">
          <div className="row between" style={{ marginBottom: 10 }}><b>Invite to {group.name}</b><span className="x" onClick={() => setShowInvite(false)}>×</span></div>
          <div className="linkbox" onClick={copy}>🔗 {shareUrl}</div>
          <div className="share">
            <button onClick={copy}><span className="ic">📋</span>Copy</button>
            <a href={`sms:?&body=${encodeURIComponent(full)}`}><span className="ic">💬</span>Message</a>
            <a href={`https://wa.me/?text=${encodeURIComponent(full)}`} target="_blank" rel="noreferrer"><span className="ic">🟢</span>WhatsApp</a>
            <button onClick={nativeShare}><span className="ic">⤴️</span>More</button>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>They register with their number, then join automatically.</p>
        </div>
      )}

      <Champions winners={w} />

      {!data && <p className="muted">Loading…</p>}

      {data?.members.map((m, i) => (
        <div key={m.id} className="card" style={{ animationDelay: `${i * 0.04}s` }}>
          <div className="row between">
            <div className="row">
              <div style={{ width: 22, textAlign: 'center', fontWeight: 800, fontSize: i < 3 ? 18 : 14, color: '#9aa0b2' }}>{rankIcon(i)}</div>
              <Avatar name={m.name} src={m.avatar} />
              <div><div style={{ fontWeight: 700 }}>{m.name}{m.isMe ? ' · you' : ''}</div>
                <div className="muted" style={{ fontSize: 13 }}>{m.done}/{m.total} done{m.verified ? ` · ${m.verified} ✅` : ''}</div></div>
            </div>
            <span className="pill fire">🔥 {m.streak}</span>
          </div>
          <div className="bar"><i style={{ width: (m.total ? (m.done / m.total) * 100 : 0) + '%' }} /></div>
          {!m.isMe && m.todos && m.todos.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {m.todos.map(t => (
                <div className="todo" key={t.id}>
                  <div className={'box' + (t.done ? ' on' : '')}>{t.done ? '✓' : ''}</div>
                  {t.text ? <span className={'txt' + (t.done ? ' done' : '')}>{t.text}</span> : <span className="txt blur">private task</span>}
                  {t.verified && <span className="tag">✅</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

            <h2>Your list for today</h2>
      <p className="muted" style={{ margin: '-4px 0 0', fontSize: 13 }}>🔒 Each task is hidden from your crew by default — flip 🙈 to share it. Your crew always sees your score.</p>
      <div className="card">
        <div className="row" style={{ gap: 8 }}>
          <input className="inp" style={{ margin: 0 }} placeholder="Add a task…" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} />
          <button className="btn sm" onClick={add}>Add</button>
        </div>
        {me?.todos.map(t => (
          <div className="todo" key={t.id}>
            <div className={'box' + (t.done ? ' on' : '')} onClick={() => toggle(t)}>{t.done ? '✓' : ''}</div>
            <span className={'txt' + (t.done ? ' done' : '')}>{t.text}</span>
            {t.verified && <span className="tag">✅</span>}
            {t.done && !t.verified && (<label className="icbtn">📷<input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => e.target.files[0] && verify(t, e.target.files[0])} /></label>)}
            <span className="row" style={{ gap: 5, flex: 'none' }} title={t.blurred ? 'Hidden from crew' : 'Visible to crew'}>
              <span style={{ fontSize: 14 }}>{t.blurred ? '🙈' : '👁️'}</span>
              <div className={'switch sm' + (t.blurred ? ' on' : '')} onClick={() => toggleBlur(t)}><i /></div>
            </span>
            <span className="x" onClick={() => del(t)}>×</span>
          </div>
        ))}
        {me && me.todos.length === 0 && <p className="empty" style={{ paddingBottom: 0 }}>Nothing yet — add your first task ☝️</p>}
      </div>
    </div>
  );
}

/* ---------- CHECK-IN ---------- */
function Checkin({ token, flash }) {
  const [todos, setTodos] = useState([]); const [verifiable, setVerifiable] = useState({}); const [loaded, setLoaded] = useState(false);
  const load = useCallback(async () => {
    try {
      const d = await api('/checkin-todos', { token }); setTodos(d.todos); setLoaded(true);
      const pending = d.todos.filter(t => !t.done);
      if (pending.length) { const r = await api('/should-verify', { method: 'POST', token, body: { texts: pending.map(t => t.text) } }); const map = {}; pending.forEach((t, i) => { map[t._id] = r.verifiable[i]; }); setVerifiable(map); }
    } catch (e) { flash(e.message); setLoaded(true); }
  }, [token]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const toggle = async (t) => { try { await api('/toggle-todo', { method: 'POST', token, body: { todoId: t._id, done: !t.done } }); load(); } catch (e) { flash(e.message); } };
  const verify = async (t, file) => { flash('🔍 Checking photo…'); try { const imageBase64 = await fileToB64(file); const d = await api('/verify-photo', { method: 'POST', token, body: { todoId: t._id, imageBase64, mediaType: file.type } }); flash(d.verified ? '✅ Verified!' : '❌ ' + (d.reason || 'Not verified')); load(); } catch (e) { flash(e.message); } };
  const doneCount = todos.filter(t => t.done).length; const allDone = loaded && todos.length > 0 && doneCount === todos.length;
  return (
    <div style={{ paddingTop: 30 }}>
      <div className="center"><div style={{ fontSize: 44 }}>🌙</div><h1 style={{ marginTop: 6 }}>Tonight’s check-in</h1></div>
      <div className="card hero center"><div style={{ display: 'inline-block' }}><Ring done={doneCount} total={todos.length} size={92} stroke={9} label="done" /></div><p className="muted" style={{ margin: '10px 0 0' }}>Tap each task you finished today.</p></div>
      {!loaded && <p className="muted center">Loading…</p>}
      {loaded && todos.length === 0 && <div className="card"><p className="empty" style={{ padding: 0 }}>No tasks for today — nice and clear.</p></div>}
      {todos.length > 0 && (
        <div className="card">{todos.map(t => (
          <div className="todo" key={t._id}>
            <div className={'box' + (t.done ? ' on' : '')} onClick={() => toggle(t)}>{t.done ? '✓' : ''}</div>
            <span className={'txt' + (t.done ? ' done' : '')}>{t.text}</span>
            {t.verified ? <span className="tag">✅</span> : verifiable[t._id] ? (
              <label className="icbtn" style={{ width: 'auto', padding: '0 12px', gap: 4 }}>📷 verify<input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => e.target.files[0] && verify(t, e.target.files[0])} /></label>) : null}
          </div>))}</div>
      )}
      {allDone && (<div className="card center" style={{ background: 'linear-gradient(135deg,#e7f8ef,#f1fbf5)', borderColor: '#bfeccd' }}><div style={{ fontSize: 40 }}>🎉</div><div style={{ fontWeight: 800, fontSize: 18 }}>All done. Streak secured.</div></div>)}
    </div>
  );
}

/* ---------- SETTINGS ---------- */
function Settings({ token, user, flash, onUpdateUser, logout, back }) {
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [optIn, setOptIn] = useState(true);
  const [blur, setBlur] = useState(true);
  const [avatar, setAvatar] = useState('');
  const onPickAvatar = async (e) => { const f = e.target.files && e.target.files[0]; if (!f) return; try { setAvatar(await resizeImage(f)); } catch (err) { } };
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    api('/me', { token }).then(d => { setName(d.user.name || ''); setPhone(d.user.phone || ''); setOptIn(d.user.smsOptIn !== false); setBlur(d.user.blurTasks !== false); setAvatar(d.user.avatar || ''); }).catch(() => { });
  }, [token]);

  const save = async () => {
    setBusy(true);
    try { const d = await api('/update-profile', { method: 'POST', token, body: { name, avatar } }); onUpdateUser(d.user); flash('Saved'); }
    catch (e) { flash(e.message); } finally { setBusy(false); }
  };
  const toggleReminder = async () => {
    const v = !optIn; setOptIn(v);
    try { await api('/update-profile', { method: 'POST', token, body: { smsOptIn: v } }); }
    catch (e) { flash(e.message); setOptIn(!v); }
  };
  const toggleBlur = async () => {
    const v = !blur; setBlur(v);
    try { await api('/update-profile', { method: 'POST', token, body: { blurTasks: v } }); }
    catch (e) { flash(e.message); setBlur(!v); }
  };
  const del = async () => { try { await api('/delete-account', { method: 'POST', token }); logout(); } catch (e) { flash(e.message); } };

  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="topbar"><span className="link" onClick={back}>‹ Profile</span><b>Account settings</b><span style={{ width: 44 }} /></div>

      <h2>Profile</h2>
      <div className="card">
        <div className="row" style={{ gap: 14, marginBottom: 6 }}>
          <Avatar name={name} src={avatar} size={60} radius={18} />
          <label className="btn ghost sm" style={{ width: 'auto' }}>Change photo
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickAvatar} />
          </label>
        </div>
        <label className="cap">Display name</label>
        <input className="inp" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
        <label className="cap">Phone</label>
        <input className="inp" value={phone} readOnly style={{ opacity: .6 }} />
        <button className="btn" disabled={busy || !name.trim()} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
      </div>

      <h2>Notifications</h2>
      <div className="card">
        <div className="rowset">
          <div><b>Nightly check-in text</b><div className="muted" style={{ fontSize: 13 }}>An SMS reminder each evening.</div></div>
          <div className={'switch' + (optIn ? ' on' : '')} onClick={toggleReminder}><i /></div>
        </div>
      </div>

      <h2>Privacy</h2>
      <div className="card">
        <div className="rowset">
          <div><b>Blur new tasks by default</b><div className="muted" style={{ fontSize: 13 }}>New tasks start hidden from your crew. You can flip any task on its own.</div></div>
          <div className={'switch' + (blur ? ' on' : '')} onClick={toggleBlur}><i /></div>
        </div>
      </div>

      <h2>Account</h2>
      <div className="card">
        <button className="btn ghost" onClick={logout}>Log out</button>
        {!confirmDel ? (
          <button className="btn ghost" style={{ marginTop: 10, color: 'var(--danger)' }} onClick={() => setConfirmDel(true)}>Delete account</button>
        ) : (
          <div style={{ marginTop: 10 }}>
            <p className="muted" style={{ fontSize: 13 }}>This permanently deletes your account, tasks, and removes you from all crews.</p>
            <button className="btn" style={{ background: 'linear-gradient(135deg,#ff7a7a,#e23b3b)', boxShadow: 'none' }} onClick={del}>Yes, delete everything</button>
            <button className="btn ghost" style={{ marginTop: 8 }} onClick={() => setConfirmDel(false)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- ADMIN ---------- */
function Admin() {
  const [key, setKey] = useState(''); const [data, setData] = useState(null); const [err, setErr] = useState('');
  const load = async () => { setErr(''); try { const r = await api('/admin/data?key=' + encodeURIComponent(key)); setData(r); } catch (e) { setErr(e.message); } };
  if (!data) return (
    <div style={{ paddingTop: 60 }}><h1>Admin</h1>
      <div className="card"><input className="inp" placeholder="admin key" value={key} onChange={e => setKey(e.target.value)} />{err && <div className="err">{err}</div>}<button className="btn" onClick={load}>Enter</button></div>
    </div>
  );
  return (
    <div style={{ paddingTop: 24 }}><h1>Admin</h1>
      <div className="statgrid" style={{ marginTop: 16 }}>
        <div className="stat"><b>{data.users.length}</b><span className="muted">Users</span></div>
        <div className="stat"><b>{data.groups.length}</b><span className="muted">Crews</span></div>
        <div className="stat"><b>{data.todos.length}</b><span className="muted">Tasks today</span></div>
        <div className="stat"><b>{data.todos.filter(t => t.done).length}</b><span className="muted">Done today</span></div>
      </div>
      <h2>Users</h2>
      {data.users.map(u => (<div key={u._id} className="card row between" style={{ padding: 14 }}><div className="row"><div className="avatar" style={avatarStyle(u.name)}>{(u.name || '?')[0].toUpperCase()}</div><div><b>{u.name}</b><div className="muted" style={{ fontSize: 13 }}>{u.phone}</div></div></div><span className="pill fire">🔥 {u.streak || 0}</span></div>))}
      <h2>Crews</h2>
      {data.groups.map(g => (<div key={g._id} className="card" style={{ padding: 14 }}><div className="row" style={{ gap: 8 }}><b>{g.name}</b>{g.isPublic && <span className="badge">Public</span>}</div><div className="muted" style={{ fontSize: 13 }}>{g.code} · {g.memberIds.length} members</div></div>))}
    </div>
  );
}